import { convexQuery, useConvexAction } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { IconCreditCardOutline18 } from 'nucleo-ui-outline-18'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  SettingsPage,
  SettingsReadOnlyValue,
  SettingsRow,
  SettingsSection,
} from '~/components/settings/settings-page'
import { Button } from '~/components/ui/button'
import { SegmentedControl, SegmentedControlItem } from '~/components/ui/segmented-control'
import { Spinner } from '~/components/ui/spinner'
import { PRO_PRICE_LABEL } from '~/lib/billing'
import { capture } from '~/lib/posthog'
import { titleFor } from '~/lib/route-title'
import { useWorkspace } from '~/lib/workspace-context'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/_authenticated/$workspaceSlug/settings/billing')({
  component: BillingSettings,
  head: () => ({ meta: [{ title: titleFor(msg`Billing`) }] }),
  // Stripe Checkout bounces back here with an outcome marker.
  validateSearch: (search: Record<string, unknown>): { checkout?: 'success' | 'canceled' } =>
    search.checkout === 'success' || search.checkout === 'canceled'
      ? { checkout: search.checkout }
      : {},
})

function BillingSettings() {
  const { t, i18n } = useLingui()
  const workspace = useWorkspace()
  const navigate = useNavigate()
  const { checkout } = Route.useSearch()
  const [interval, setInterval] = useState<'month' | 'year'>('month')
  const { data: billing } = useQuery(
    convexQuery(api.billing.getSubscription, { workspaceId: workspace._id })
  )
  // Mirrors the server's `requireAdminIn` gate on the checkout/portal actions.
  const isAdmin = workspace.role === 'admin'
  const adminNote = isAdmin ? undefined : <Trans>Only workspace admins can manage billing.</Trans>

  const createCheckout = useConvexAction(api.billing.createCheckoutSession)
  const createPortal = useConvexAction(api.billing.createPortalSession)
  const checkoutMutation = useMutation({
    mutationFn: async (args: { interval: 'month' | 'year'; workspaceId: typeof workspace._id }) => {
      // Captured BEFORE the redirect, not between it and the action: this
      // handler ends in a full-page navigation, and an event queued on the
      // way out is the one posthog-js is most likely to drop.
      capture('checkout_started', { billing_interval: args.interval })
      const session = await createCheckout(args)
      // Full-page redirect to Stripe's hosted checkout.
      window.location.assign(session.url)
    },
    onError: () => toast.error(t`Could not open the checkout. Please try again.`),
  })
  const portalMutation = useMutation({
    mutationFn: async (args: { workspaceId: typeof workspace._id }) => {
      capture('billing_portal_opened')
      const session = await createPortal(args)
      window.location.assign(session.url)
    },
    onError: () => toast.error(t`Could not open the billing portal. Please try again.`),
  })
  const isBusy = checkoutMutation.isPending || portalMutation.isPending

  // Surface the checkout outcome once, then strip the marker from the URL so
  // a refresh doesn't repeat the toast.
  useEffect(() => {
    if (checkout === undefined) {
      return
    }
    if (checkout === 'success') {
      toast.success(t`Payment confirmed. Your plan will update shortly.`)
    } else {
      toast(t`Checkout canceled. Your plan is unchanged.`)
    }
    void navigate({
      params: { workspaceSlug: workspace.slug },
      replace: true,
      search: {},
      to: '/$workspaceSlug/settings/billing',
    })
  }, [checkout, navigate, t, workspace.slug])

  // `getSubscription` never returns null — `undefined` is the only pending
  // state, and the discriminated union below covers everything else.
  if (billing === undefined) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-5" />
      </div>
    )
  }

  if (billing.status === 'disabled') {
    return (
      <SettingsPage>
        <SettingsSection bare icon={IconCreditCardOutline18} title={t`Plan`}>
          <p className="text-muted-foreground text-sm">
            <Trans>Billing is not configured on this deployment.</Trans>
          </p>
        </SettingsSection>
      </SettingsPage>
    )
  }

  if (billing.status === 'free') {
    return (
      <SettingsPage>
        <SettingsSection description={adminNote} icon={IconCreditCardOutline18} title={t`Plan`}>
          <SettingsRow label={t`Current plan`}>
            <SettingsReadOnlyValue>
              <Trans>Pessoal</Trans>
            </SettingsReadOnlyValue>
          </SettingsRow>
          <SettingsRow label={t`Seats`}>
            <SettingsReadOnlyValue>
              <Trans>
                {billing.memberCount} of {billing.seatLimit} in use
              </Trans>
            </SettingsReadOnlyValue>
          </SettingsRow>
        </SettingsSection>

        <SettingsSection
          description={
            <Trans>Unlimited team members, billed per person. Save 15% when you pay yearly.</Trans>
          }
          icon={IconCreditCardOutline18}
          title={t`Upgrade to Profissional`}
        >
          <SettingsRow label={t`Billing cycle`}>
            <div className="flex flex-col gap-2">
              <SegmentedControl
                aria-label={t`Billing cycle`}
                onValueChange={(value) => {
                  if (value === 'month' || value === 'year') {
                    setInterval(value)
                  }
                }}
                value={interval}
              >
                <SegmentedControlItem value="month">
                  <Trans>Monthly</Trans>
                </SegmentedControlItem>
                <SegmentedControlItem value="year">
                  <Trans>Yearly</Trans>
                </SegmentedControlItem>
              </SegmentedControl>
              <p className="text-muted-foreground text-sm">
                {interval === 'month' ? (
                  <Trans>{PRO_PRICE_LABEL.month} per person, per month.</Trans>
                ) : (
                  <Trans>{PRO_PRICE_LABEL.year} per person, per year.</Trans>
                )}
              </p>
            </div>
          </SettingsRow>
          <SettingsRow
            description={<Trans>You'll be redirected to our payment partner, Stripe.</Trans>}
            label={t`Checkout`}
          >
            <Button
              disabled={!isAdmin || isBusy}
              onClick={() => checkoutMutation.mutate({ interval, workspaceId: workspace._id })}
            >
              <Trans>Upgrade to Profissional</Trans>
            </Button>
          </SettingsRow>
        </SettingsSection>
      </SettingsPage>
    )
  }

  const renewalDate = i18n.date(new Date(billing.currentPeriodEnd * 1000), {
    dateStyle: 'long',
  })

  return (
    <SettingsPage>
      <SettingsSection description={adminNote} icon={IconCreditCardOutline18} title={t`Plan`}>
        <SettingsRow label={t`Current plan`}>
          <SettingsReadOnlyValue>
            {billing.interval === 'year' ? (
              <Trans>Profissional (yearly)</Trans>
            ) : (
              <Trans>Profissional (monthly)</Trans>
            )}
          </SettingsReadOnlyValue>
        </SettingsRow>
        <SettingsRow
          description={<Trans>Billed per person. Seats adjust as people join or leave.</Trans>}
          label={t`Seats`}
        >
          <SettingsReadOnlyValue>{billing.memberCount}</SettingsReadOnlyValue>
        </SettingsRow>
        <SettingsRow label={billing.cancelAtPeriodEnd ? t`Ends on` : t`Renews on`}>
          <SettingsReadOnlyValue>{renewalDate}</SettingsReadOnlyValue>
        </SettingsRow>
        {billing.subscriptionStatus === 'past_due' ? (
          <SettingsRow label={t`Payment`}>
            <p className="text-destructive text-sm">
              <Trans>
                The last payment failed. Update your card in the billing portal to keep
                Profissional.
              </Trans>
            </p>
          </SettingsRow>
        ) : null}
        <SettingsRow
          description={
            <Trans>
              Invoices, payment methods, and cancellations are managed securely by Stripe.
            </Trans>
          }
          label={t`Billing portal`}
        >
          <Button
            disabled={!isAdmin || isBusy}
            onClick={() => portalMutation.mutate({ workspaceId: workspace._id })}
            variant="secondary"
          >
            <Trans>Manage billing</Trans>
          </Button>
        </SettingsRow>
      </SettingsSection>
    </SettingsPage>
  )
}
